import { useMemo, useState, type JSX } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Seo } from '@/components/seo';
import { PublicPageLayout, PublicPageSection } from '@/layouts/PublicPageLayout';
import { PublicPageHeading } from '@/components/public/PublicPageHeading';
import { LeadForm } from '@/routes/publicProperties/components/LeadForm';
import { useSiteSettingsDetail } from '@/lib/query/resources/siteSettings';
import type { LeadCaptureContent } from '@/lib/types/siteSettings';
import { DEFAULT_LEAD_FORM_CONTENT } from '@/routes/publicProperties/components/LeadForm';

const DEFAULT_LEAD_CAPTURE_CONTENT: LeadCaptureContent = {
  eyebrow: 'Leasing Concierge',
  title: 'Unlock your next workspace',
  description:
    'Tell us about your space requirements and our leasing team will connect with tailored options across the Strashin Developments portfolio.',
  thankYou:
    'Our leasing team has received your request and will reach out within 48 hours with tailored options. If you need to update any details, reply to the confirmation email.',
  seo: {
    title: 'Schedule a Tour with Strashin Developments',
    description:
      'Tell us about your space requirements and our leasing team will connect with tailored options across the Strashin Developments portfolio.',
  },
  thankYouTitle: 'Thank you',
  benefitsHeading: 'Why partner with Strashin',
  benefits: [
    {
      title: 'Operational transparency',
      description:
        'We surface availability, pricing, and sustainability data up front so you can evaluate options quickly.',
    },
    {
      title: 'Curated tours',
      description:
        'Our team schedules on-site tours and virtual walk-throughs tailored to your campaign goals.',
    },
    {
      title: 'End-to-end support',
      description:
        'From first contact to lease negotiation and tenant onboarding, we stay aligned with your milestones.',
    },
  ],
  stats: [
    { value: '48 hrs', label: 'Average response time on new leasing requests.' },
    { value: '6M+ sqft', label: 'Managed across Strashin’s sustainable portfolio.' },
    { value: '98%', label: 'Tenant retention rate thanks to proactive service.' },
  ],
  form: DEFAULT_LEAD_FORM_CONTENT,
};

function preferText(value: string | null | undefined, fallback: string): string {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value;
  }
  return fallback;
}

function preferList<T>(value: T[] | null | undefined, fallback: T[]): T[] {
  if (Array.isArray(value) && value.length > 0) {
    return value;
  }
  return fallback;
}

export function LeadLanding(): JSX.Element {
  const [searchParams] = useSearchParams();
  const initialParams = useMemo(() => {
    return {
      desiredPropertyId: searchParams.get('propertyId') ?? null,
      desiredUnitId: searchParams.get('unitId') ?? null,
      campaignSource: searchParams.get('source') ?? null,
      campaignMedium: searchParams.get('medium') ?? null,
    };
  }, [searchParams]);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const siteSettingsQuery = useSiteSettingsDetail();
  const leadCopy = siteSettingsQuery.data?.leadCapture ?? DEFAULT_LEAD_CAPTURE_CONTENT;
  const bookingEmail = siteSettingsQuery.data?.bookingEmail ?? 'leasing@strashindevelopments.com';
  const resolvedCopy: LeadCaptureContent = {
    ...DEFAULT_LEAD_CAPTURE_CONTENT,
    eyebrow: preferText(leadCopy.eyebrow, DEFAULT_LEAD_CAPTURE_CONTENT.eyebrow),
    title: preferText(leadCopy.title, DEFAULT_LEAD_CAPTURE_CONTENT.title),
    description: preferText(leadCopy.description, DEFAULT_LEAD_CAPTURE_CONTENT.description),
    thankYou: preferText(leadCopy.thankYou, DEFAULT_LEAD_CAPTURE_CONTENT.thankYou),
    thankYouTitle: preferText(leadCopy.thankYouTitle, DEFAULT_LEAD_CAPTURE_CONTENT.thankYouTitle),
    benefitsHeading: preferText(
      leadCopy.benefitsHeading,
      DEFAULT_LEAD_CAPTURE_CONTENT.benefitsHeading,
    ),
    benefits: preferList(leadCopy.benefits, DEFAULT_LEAD_CAPTURE_CONTENT.benefits),
    stats: preferList(leadCopy.stats, DEFAULT_LEAD_CAPTURE_CONTENT.stats),
    seo: {
      title: preferText(leadCopy.seo.title, DEFAULT_LEAD_CAPTURE_CONTENT.seo.title),
      description: preferText(
        leadCopy.seo.description,
        DEFAULT_LEAD_CAPTURE_CONTENT.seo.description,
      ),
    },
    form: leadCopy.form ?? DEFAULT_LEAD_CAPTURE_CONTENT.form,
  };

  return (
    <PublicPageLayout>
      <Seo
        title={resolvedCopy.seo.title}
        description={resolvedCopy.seo.description}
        canonicalPath="/campaigns/lead"
      />

      <PublicPageSection tone="base" spacing="lg">
        <PublicPageHeading
          as="h1"
          eyebrow={resolvedCopy.eyebrow}
          title={resolvedCopy.title}
          description={resolvedCopy.description}
        />
      </PublicPageSection>

      <PublicPageSection tone="muted" spacing="lg">
        <div className="grid gap-8 md:gap-12 lg:grid-cols-[2fr_3fr] lg:items-start">
          <article className="flex flex-col gap-6">
            <h2 className="text-2xl font-semibold text-text-primary">
              {resolvedCopy.benefitsHeading}
            </h2>
            <ul className="flex flex-col gap-4 text-sm leading-relaxed text-text-primary/70">
              {resolvedCopy.benefits.map((benefit) => (
                <li key={benefit.title}>
                  <strong>{benefit.title}:</strong> {benefit.description}
                </li>
              ))}
            </ul>
          </article>

          {isSubmitted ? (
            <div className="flex flex-col gap-4 rounded-3xl bg-white p-6 shadow-2xl md:p-8">
              <h2 className="text-2xl font-semibold text-brand-primary">
                {resolvedCopy.thankYouTitle}
              </h2>
              <p className="text-sm leading-relaxed text-text-primary/70">
                {resolvedCopy.thankYou}{' '}
                {bookingEmail ? `You can also reach us at ${bookingEmail}.` : ''}
              </p>
            </div>
          ) : (
            <LeadForm
              campaignSource={initialParams.campaignSource}
              campaignMedium={initialParams.campaignMedium}
              desiredPropertyId={initialParams.desiredPropertyId}
              desiredUnitId={initialParams.desiredUnitId}
              formContent={resolvedCopy.form}
              onSuccess={() => {
                setIsSubmitted(true);
              }}
            />
          )}
        </div>
      </PublicPageSection>

      {isSubmitted ? null : (
        <PublicPageSection tone="contrast" spacing="md">
          <div className="grid gap-6 text-white lg:grid-cols-3">
            {resolvedCopy.stats.map((stat) => (
              <div key={stat.label} className="flex flex-col gap-2">
                <span className="text-3xl font-semibold">{stat.value}</span>
                <p className="text-sm text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>
        </PublicPageSection>
      )}
    </PublicPageLayout>
  );
}
